import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { SqlComposer } from 'src/app/utils/query/sql-composer';
import { BiVizRealtimePresenceDatasource } from '../sql-maps/biviz-realtime-presence-sql-map';
import { BiVizRealtimeStatsDatasource } from '../sql-maps/biviz-realtime-stats-sql-map';

export interface RealtimeStatsPurchaseFailedDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

export class RealtimeStatsPurchaseFailedDataLoaderFactory extends Factory<
    DataLoader,
    RealtimeStatsPurchaseFailedDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'RealtimeStatsPurchaseFailedDataLoaderFactory';

    getInstance(options: RealtimeStatsPurchaseFailedDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const biVizRealtimeStats = BiVizRealtimeStatsDatasource;

                // clone queryContext to avoid modifying the original object
                var queryContextPurchaseTotal = structuredClone(queryContext);

                // remove REALTIME_PURCHASE_ERROR from queryContext
                // Some metrics are not supporting REALTIME_PURCHASE_ERROR, so we need to remove it from queryContext when calculating those metrics to avoid errors.
                queryContextPurchaseTotal.datasourceColumns =
                    queryContextPurchaseTotal.datasourceColumns.filter(
                        (col) => col.id !== 'REALTIME_PURCHASE_ERROR'
                    );

                // identify columns not supported in datasource
                const biVizRealtimeStatsDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimeStats.sqlMap.columns
                    );
                const biVizRealtimeStatsPurchaseTotalDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContextPurchaseTotal.datasourceColumns,
                        biVizRealtimeStats.sqlMap.columns
                    );

                let latestHourSqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_hours_up_to_latest is true, then we should filter hours to be up to the latest hour
                    if (queryContext.settings.realtimeHoursUpToLatest) {
                        const latest_hour = await DataLoader.runQuery(
                            `SELECT EXTRACT(HOUR FROM MAX(time_hour)) AS latest_hour FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLHoursUpToLatest(
                                    latest_hour.result[0].latest_hour
                                ),
                            ],
                        };
                    }
                    return;
                })();

                let currentWeekdaySqlToken:
                    | Partial<ICompiledSQLTokens>
                    | undefined = await (async () => {
                    // if settings.realtime_current_weekday is true, then we should filter weekday to be the current weekday
                    if (queryContext.settings.realtimeCurrentWeekday) {
                        const current_weekday = await DataLoader.runQuery(
                            `SELECT FORMAT_DATETIME("%A", MAX(time_hour)) AS current_weekday FROM \`${generateTableFullPath(
                                options.gcpProjectId,
                                biVizRealtimeStats.sqlMap.dataset,
                                biVizRealtimeStats.sqlMap.tableId
                            )}\``,
                            options.queryCacheService
                        );
                        queryContext.settings.realtimeCurrentWeekday =
                            current_weekday.result[0].current_weekday;

                        return {
                            where: [
                                BiVizRealtimeStatsDatasource.compileWhereSQLCurrentWeekday(
                                    current_weekday.result[0].current_weekday
                                ),
                            ],
                        };
                    }
                    return;
                })();

                const customSQLTokens: Partial<ICompiledSQLTokens> =
                    mergeSqlTokensArray(
                        [latestHourSqlToken, currentWeekdaySqlToken]
                            // filter out undefined values
                            .filter((x) => x) as Partial<ICompiledSQLTokens>[]
                    );

                const biVizRealtimeStatsDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimeStatsDsColumns.missing
                    );

                const purchaseFailedCompiledSQL =
                    biVizRealtimeStats.sqlStatementsCompiler.compileSQLStatements(
                        biVizRealtimeStatsDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const purchaseTotalCompiledSQL =
                    biVizRealtimeStats.compileSQLStatements(
                        biVizRealtimeStatsPurchaseTotalDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const biVizRealtimePresence = BiVizRealtimePresenceDatasource;

                const biVizRealtimePresenceDsColumns =
                    DataLoader.separatePresentAndMissingDims(
                        queryContext.datasourceColumns,
                        biVizRealtimePresence.sqlMap.columns
                    );

                const biVizRealtimePresenceDsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(
                        biVizRealtimePresenceDsColumns.missing
                    );

                const presenceCompiledSQL =
                    biVizRealtimePresence.compileSQLStatements(
                        biVizRealtimePresenceDsColumns.present,
                        queryContext.settings,
                        options,
                        customSQLTokens
                    );

                const getMetricSQL = (
                    metricSQLSource: string,
                    metricSQL: string,
                    normalizer: string,
                    normalizedMode: IQueryContext['settings'][QueryContextSettingID.REALTIME_NORMALIZE_METRICS]
                ): string => {
                    return normalizedMode != 'none'
                        ? `SAFE_DIVIDE(${metricSQLSource}.${metricSQL}, ${normalizer}) AS ${metricSQL}`
                        : `${metricSQLSource}.${metricSQL}`;
                };

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH purchase_failed_base AS (
  SELECT *
    , ${purchaseFailedCompiledSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
  FROM(
    SELECT ds.*
      , ${purchaseFailedCompiledSQL.datasourceSpecificSplitSelect}
      ${purchaseFailedCompiledSQL.cohortSplitSelect}
      ${
          // Presence supports only common filters/splits, not query-specific ones like REALTIME_PURCHASE_ERROR.
          // Because of that, we build presence_split_group here, so later we can join presence using a matching split key.
          presenceCompiledSQL.splitSelect != ''
              ? `${presenceCompiledSQL.splitSelect.replace(
                    'AS split_group',
                    'AS presence_split_group'
                )}`
              : ''
      }
    FROM \`${generateTableFullPath(
        options.gcpProjectId,
        biVizRealtimeStats.sqlMap.dataset,
        biVizRealtimeStats.sqlMap.tableId
    )}\` ds
    ${purchaseFailedCompiledSQL.where}
  )  
)

, purchase_failed AS (
  SELECT
    ${purchaseFailedCompiledSQL.datetimeTrunc()} AS datetime
    , ${purchaseFailedCompiledSQL.splitGroupBy}
    ${purchaseFailedCompiledSQL.cohortSplitGroupBy}
    ${presenceCompiledSQL.splitGroupBy != '' ? 'presence_split_group,' : ''}
    NULLIF(SUM(PurchaseFailed_count), 0) AS PurchaseFailed_count
    , NULLIF(SUM(PurchaseFailed_users_count), 0) AS PurchaseFailed_users_count
  FROM purchase_failed_base
  ${SqlComposer.GROUP_BY([
      `datetime`,
      purchaseFailedCompiledSQL.splitGroup,
      purchaseFailedCompiledSQL.cohortSplitGroupAlias,
      presenceCompiledSQL.splitGroupBy != '' ? 'presence_split_group' : '',
  ])}
)

, purchase_total AS (
  SELECT datetime
    , ${purchaseFailedCompiledSQL.splitSelectConcatCohortAndDatasourceSpecificColumn}
    PurchaseComplete_count
    , PurchaseComplete_users_count
    , PurchaseFailed_count
    , PurchaseFailed_users_count
  FROM (
    SELECT
      ${purchaseFailedCompiledSQL.datetimeTrunc()} AS datetime
      , ${purchaseFailedCompiledSQL.cohortSplitGroupBy}
      SUM(PurchaseComplete_count) AS PurchaseComplete_count
      , SUM(PurchaseComplete_users_count) AS PurchaseComplete_users_count
      , SUM(PurchaseFailed_count) AS PurchaseFailed_count
      , SUM(PurchaseFailed_users_count) AS PurchaseFailed_users_count
    FROM (
      SELECT 
        ${
            purchaseTotalCompiledSQL.splitGroupBy != ''
                ? `${purchaseTotalCompiledSQL.splitSelect.replace(
                      'AS split_group',
                      'AS cohort_split_group'
                  )}`
                : ''
        }
        time_hour
        , NULLIF(SUM(PurchaseComplete_count), 0) AS PurchaseComplete_count
        , NULLIF(SUM(PurchaseComplete_users_count), 0) AS PurchaseComplete_users_count
        , NULLIF(SUM(PurchaseFailed_count), 0) AS PurchaseFailed_count
        , NULLIF(SUM(PurchaseFailed_users_count), 0) AS PurchaseFailed_users_count
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          biVizRealtimeStats.sqlMap.dataset,
          biVizRealtimeStats.sqlMap.tableId
      )}\` ds
      ${purchaseTotalCompiledSQL.where}
      GROUP BY ${purchaseFailedCompiledSQL.cohortSplitGroupBy}time_hour
    )
    GROUP BY ${purchaseFailedCompiledSQL.cohortSplitGroupBy}datetime
  )
  ${
      // if split PurchaseFailed event specific columns are selected, then we need to add them to the PurchaseComplete query
      purchaseFailedCompiledSQL.is_in_context_split_datasource_specific_columns
          ? `CROSS JOIN (SELECT DISTINCT ${purchaseFailedCompiledSQL.datasourceSpecificSplitGroupAlias} FROM purchase_failed_base)`
          : ''
  }
)

, presence AS (
  SELECT ${presenceCompiledSQL.datetimeTrunc()} AS datetime
    , ${presenceCompiledSQL.splitSelect}
    NULLIF( SUM( installers_count), 0) AS installers_count
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      biVizRealtimePresence.sqlMap.dataset,
      biVizRealtimePresence.sqlMap.tableId
  )}\` ds
  ${presenceCompiledSQL.where}
  ${SqlComposer.GROUP_BY([`datetime`, presenceCompiledSQL.splitGroup])}
)

SELECT purchase_failed.datetime
  ${purchaseFailedCompiledSQL.splitSelect ? ', purchase_failed.split_group' : ''}
  , ${getMetricSQL(
      'purchase_failed',
      `PurchaseFailed_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  , ${getMetricSQL(
      'purchase_failed',
      `PurchaseFailed_users_count`,
      'presence.installers_count',
      queryContext.settings[QueryContextSettingID.REALTIME_NORMALIZE_METRICS] ??
          'none'
  )}
  
  , SAFE_DIVIDE(
      COALESCE(purchase_failed.PurchaseFailed_count, 0),
      COALESCE(purchase_total.PurchaseFailed_count, 0) + COALESCE(purchase_total.PurchaseComplete_count, 0)
    ) AS PurchaseFailed_rate
  , SAFE_DIVIDE(
      COALESCE(purchase_failed.PurchaseFailed_users_count, 0),
      COALESCE(purchase_total.PurchaseFailed_users_count, 0) + COALESCE(purchase_total.PurchaseComplete_users_count, 0)
    ) AS PurchaseFailed_users_rate
FROM purchase_failed
LEFT JOIN purchase_total
  ON purchase_total.datetime = purchase_failed.datetime
  ${purchaseFailedCompiledSQL.splitGroup ? `AND purchase_total.split_group = purchase_failed.split_group` : ''}
LEFT JOIN presence
  ON presence.datetime = purchase_failed.datetime
  ${presenceCompiledSQL.splitSelect ? `AND presence.split_group = purchase_failed.presence_split_group` : ''}
${SqlComposer.ORDER_BY(['datetime', purchaseFailedCompiledSQL.splitGroup])}
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'datetime',
                    queryContext.settings[
                        QueryContextSettingID.DATETIME_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: biVizRealtimeStatsDsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage:
                        biVizRealtimeStatsDsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
